import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import Status from '@/components/Modal/Project/Status';
import Loader from '@/components/ui/Loader/Loader';

import { getStatus } from '@/helper/getStatus';
import project from '@/api/project';

const { getProduct, getProducts } = project();

export default function Statuses() {
	const pStatus = useSelector((state) => state.content.projectStatusD);

	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { order_by: '-productid' };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['project-status', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProducts(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length;
			if (isFetched && count) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]) || [];

	const columns = [
		{
			field: 'productprefix',
			headerName: 'Kode',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productcodenumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productversion',
			headerName: 'Versjon',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'updatetimestamp',
			headerName: 'Dato',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { updatetimestamp } = params.row;
				return (
					<div className='flex flex-col'>
						<span>{format(new Date(updatetimestamp), 'yyyy/MM/dd')}</span>
					</div>
				);
			},
		},
		{
			field: 'Status',
			headerName: 'Status',
			headerAlign: 'center',
			align: 'center',
			renderCell: (params) => {
				const { status: stat } = params.row;
				const status = getStatus(pStatus, +stat);
				return status.url ? (
					<div className='flex items-center h-full'>
						<img className='m-auto' title={status.title} alt={status.title} src={status.url} />
					</div>
				) : (
					<div className='m-auto text-wrap flex items-center'>Ugyldig status</div>
				);
			},
		},
	];

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
	} = useQuery({
		queryKey: ['project', selectedRow?.productid],
		enabled: !!selectedRow,
		queryFn: () => getProduct(selectedRow?.productid),
	});
	const dataRow = useMemo(() => (selectedRow ? p?.data || null : null), [p, selectedRow]) || null;

	const onSaveStatus = () => {};

	return (
		<>
			<BDHelmet title='Project Status' description='BDS Samferdsel project status' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk i dokument her ...'
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div>
						{isFetchingP && !isFetchedP && <Loader />}

						<DataGrid
							getRowId={(row) => row.productid}
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.productid === params.row.productid) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { pageSize: 10 } },
							}}
							// rowCount={dataCount}
							paginationModel={paginationModel}
							// paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{!!dataRow && (
					<Status
						open={!!dataRow}
						onSave={onSaveStatus}
						data={dataRow}
						onCallback={() => {
							setSelectedRow(false);
							refetch();
						}}
						onClose={() => setSelectedRow(false)}
					/>
				)}
			</div>
		</>
	);
}
